<script setup>
/**
 * 「流水明细」页，逐条对齐旧版 web/21.028838b9676ae40f5420.js 里的
 * app-finance 组件（类 r、模板工厂 y + 子模板 _/C/x/O/M/k/P/v、样式 d）。
 *
 * 页面标题旧版是「流水明细」。注意个人中心的菜单项写的是「交易明细」，
 * 两处文案本来就不一样，这里按页面自己的标题走。
 *
 * 旧版类 r：
 *   字段：startTime=''、endTime=''、type='0'、paramShow=true、page
 *   ngOnInit → getLogType(); getLogList()
 *   getLogType(): GET /finance/log/type → typeList（后端返回 MemberFinance::$type_lists，是 {key: 文案} 字典）
 *   getLogList(n): POST /finance/log/lists {page, start, end, type}
 *     lists = page_index === 1 ? data.lists : lists.concat(data.lists)
 *   changParam(): page_index = 1; getLogList()
 *   doRefresh(n): page_index = 1; getLogList(n)
 *   doInfinite(n): page_index += 1；超过 page_count 就 toast「已经最后一页了」
 *
 * 注意 startTime / endTime 一直是空字符串 —— 旧版模板里只有「类型」一个筛选，
 * 没有日期选择器，所以这两个参数始终传空。
 *
 * 模板：
 *   ion-header > ion-toolbar：ion-button[routerLink="/tabs/user"] + ion-title「流水明细」
 *   ion-content（没有 padding 属性）
 *     ion-list
 *       ion-list-header (click)="paramShow=!paramShow" 「筛选」
 *         *ngIf="paramShow"  → ion-icon[name=arrow-down]
 *         *ngIf="!paramShow" → ion-icon[name=arrow-up]
 *       *ngIf="paramShow && typeList" → ion-item：ion-label「类型:」+ ion-select
 *           ion-select-option value="0"「不限」+ ngFor typeList
 *     *ngIf="lists" → div.log-list > div.log-list-item
 *         div.log-info：span.reason / span.type / span.time
 *         div.log-money：span.balance-before / span.money（正数前面额外输出一个 "+"） / span.balance-after
 *     ion-refresher + ion-infinite-scroll
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from '../components/PullRefresher.vue'
import LegacySelect from '../components/LegacySelect.vue'

// 旧版 paramShow 默认 true
const paramShow = ref(true)
// 旧版 type 默认字符串 '0'（不限）
const type = ref('0')
// 旧版 typeList 初始 undefined，模板 *ngIf="paramShow && typeList"
const typeList = ref(null)
// 旧版 lists 初始 undefined，模板 *ngIf="lists"
const lists = ref(null)
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 0 })
const loading = ref(false)
const notice = ref('')

// 旧版 startTime / endTime 始终是空串（模板里没有日期筛选）
const startTime = ref('')
const endTime = ref('')

const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

/** 旧版的 ion-select-option：固定的「不限」+ typeList 字典里的每一项 */
const typeOptions = computed(() => {
  const options = [{ value: '0', label: '不限' }]
  const dict = typeList.value || {}
  Object.keys(dict).forEach((key) => { options.push({ value: String(key), label: dict[key] }) })
  return options
})

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/** 旧版 [ngClass]="{win: money>0, lost: money<0}" */
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

/** 旧版 getLogType() */
async function getLogType() {
  try {
    const response = await request('/finance/log/type')
    typeList.value = response.data || {}
  } catch (error) {
    notice.value = error.message || '加载失败'
  }
}

/** 旧版 getLogList(n=null) */
async function getLogList(event = null) {
  loading.value = true
  try {
    const response = await request('/finance/log/lists', {
      method: 'POST',
      data: { page: page.value.page_index, start: startTime.value, end: endTime.value, type: type.value },
    })
    page.value = { ...page.value, ...(response.page || {}) }
    const incoming = response.data?.lists || []
    lists.value = page.value.page_index === 1 ? incoming : [...(lists.value || []), ...incoming]
  } catch (error) {
    notice.value = error.message || '加载失败'
  } finally {
    loading.value = false
    event?.complete()
  }
}

/** 旧版 changParam() */
function changParam() {
  page.value = { ...page.value, page_index: 1 }
  void getLogList()
}

/** 旧版 doRefresh(n) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getLogList(event)
}

/** 旧版 doInfinite(n) */
function doInfinite() {
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { notice.value = '已经最后一页了'; return }
  page.value = { ...page.value, page_index: next }
  void getLogList()
}

// 旧版 ngOnInit
onMounted(() => { void getLogType(); void getLogList() })
</script>

<template>
  <div class="finance-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/user" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">流水明细</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版第一个 ion-list：筛选 -->
      <section class="ion-list">
        <div class="list-header" @click="paramShow = !paramShow">
          <span> 筛选 </span>
          <!-- 旧版 arrow-down / arrow-up 两个 ion-icon，取自 web/svg/md-arrow-down.svg 的同款方向 -->
          <svg v-if="paramShow" class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 392L100 236l28-28 128 128 128-128 28 28z" fill="currentColor" /></svg>
          <svg v-else class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 120l156 156-28 28-128-128-128 128-28-28z" fill="currentColor" /></svg>
        </div>

        <!-- 旧版 O：*ngIf="paramShow && typeList" -->
        <div v-if="paramShow && typeList" class="list-item">
          <div class="list-item-inner">
            <div class="ion-label">类型:</div>
            <LegacySelect
              v-model="type"
              :options="typeOptions"
              alert-header="类型:"
              cancel-text="取消"
              ok-text="确定"
              @change="changParam"
            />
          </div>
        </div>
      </section>

      <!-- 旧版 P：*ngIf="lists" -->
      <div v-if="lists" class="log-list">
        <div v-for="(row, index) in lists" :key="row.log_id ?? index" class="log-list-item">
          <div class="log-info">
            <span class="reason"> {{ row.reason }} </span>
            <span class="type"> {{ row.log_type_text }} </span>
            <span class="time"> {{ row.handle_time }} </span>
          </div>
          <div class="log-money">
            <span class="balance-before"> {{ fmt2(row.balance_before) }} </span>
            <span class="money" :class="signClass(row.money)">
              <!-- 旧版 M：money>0 时额外输出一个加号 -->
              <template v-if="Number(row.money) > 0">+</template>{{ fmt2(row.money) }}
            </span>
            <span class="balance-after"> {{ fmt2(row.balance_after) }} </span>
          </div>
        </div>
      </div>

      <!-- 旧版 v：ion-infinite-scroll，loadingSpinner="bubbles" loadingText="正在加载" -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
      <p v-if="notice" class="modal-notice">{{ notice }}</p>
    </PullRefresher>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
.finance-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden;cursor:pointer}
.list-header .header-arrow{width:1em;height:1em;flex:none;opacity:.54}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* --- 以下逐条照抄旧版样式表 d（chunk 21），:host → .finance-page --- */
.log-list .log-list-item{width:100%;display:flex;align-items:center;justify-content:space-between;font-size:.8rem;padding:10px;border-bottom:1px solid #ccc;line-height:2}
.log-list .log-list-item span{display:block}
.log-list .log-list-item .log-info .reason{color:#000;font-size:.8rem}
.log-list .log-list-item .log-info .type{color:#666;font-size:.8rem}
.log-list .log-list-item .log-info .time{color:#ccc;font-size:.8rem}
.log-list .log-list-item .log-money{text-align:right}
.log-list .log-list-item .log-money .balance-before{color:#666}
.log-list .log-list-item .log-money .balance-after{color:#666}
.log-list .log-list-item .log-money .money{font-size:1.2rem}
.log-list .log-list-item .log-money .win{color:red}
.log-list .log-list-item .log-money .lost{color:green}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
